/**
 * Hook usePipelinePhaseStats - Busca estatísticas das fases via cache
 * Utiliza tabela de cache para evitar COUNT em tempo real
 * Versão: 1.0.0
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger para depuração
const logger = createLogger('PipelinePhaseStats');

// Tipo das estatísticas da fase
export interface PhaseStats {
  phase_id: string;
  phase_name: string;
  phase_color: string | null;
  phase_sort_order: number;
  phase_icon: string | null;
  is_won: boolean;
  is_lost: boolean;
  open_count: number;
  total_value: number;
  deal_count: number;
}

/**
 * Hook para buscar estatísticas das fases de um pipeline
 * Utiliza tabela de cache para alta performance
 * 
 * @param pipelineId - ID do pipeline
 */
export function usePipelinePhaseStats(pipelineId: string | null) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['pipeline-phase-stats', pipelineId],
    queryFn: async () => {
      if (!pipelineId) return [];

      logger.debug(`Buscando stats do pipeline ${pipelineId}`);

      const { data, error } = await supabase.rpc('get_pipeline_phase_stats', {
        p_pipeline_id: pipelineId,
        p_tenant_hint: currentTenant?.id || null
      });

      if (error) {
        logger.error('Erro ao buscar stats do pipeline:', error);
        throw error;
      }

      logger.debug(`Recebidas ${data?.length || 0} fases com stats`);
      return (data || []) as PhaseStats[];
    },
    enabled: !!pipelineId && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  // Função para invalidar cache
  const invalidateStats = () => {
    queryClient.invalidateQueries({ 
      queryKey: ['pipeline-phase-stats', pipelineId] 
    });
  };

  return {
    phases: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    invalidateStats,
  };
}
